import { useRouter } from "next/router";
import { Button, Center, Tooltip, HStack } from "@chakra-ui/react";
import PermissionField from "components/common/permission-field";
import LinkedinSquareLogo from "components/icons/circle-icons/linkedin-square-logo";
import MailIcon from "components/icons/circle-icons/mail-icon";
import PhoneIconContact from "components/icons/circle-icons/phone-icon-contact";
import {
  CardTable,
  CardTbody,
  CardTd,
  CardTh,
  CardThead,
  CardTr,
  SkeletonRows,
} from "components/profile/card-table";
import ProfileCard, {
  ProfileCardHeader,
} from "components/profile/profile-card";
import { useRequestUpdate } from "components/profile/update-modals/request-update-modal";
import VirtualizedTableTagCollection from "components/virtualized-table/virtualized-table-tag-collection";
import usePermission from "hooks/use-permission";
import useUser from "hooks/use-user";
import { isSelectUser as isSelectUserFn } from "utils/access-group";
import type { CompanyProfile } from "types/api/company/details/overview";
import ContactInfoBtn from "../company-contacts/contact-info-btn";

interface ContactsCardProps {
  isLoaded: boolean;
  isInternational: boolean;
  maxContactsHeight: number;
  companyProfile: CompanyProfile;
  aboutHeight: number;
}

const ContactsCard = ({
  isLoaded,
  companyProfile,
  aboutHeight,
  isInternational,
  maxContactsHeight,
}: ContactsCardProps) => {
  const { openRequestUpdateModal } = useRequestUpdate();

  const router = useRouter();

  const hasCompanyOverviewContactsPermission = usePermission(
    "companyOverviewContacts"
  );
  const hasCompanyContactViewPermission = usePermission("companyContactView");
  const hasCompanyContactsPermission = usePermission("companyContacts");

  const user = useUser();
  const isFreeUser = user?.access?.group === "free";
  const isPublicUser = !user?.isLoggedIn || false;
  const isSelectUser = isSelectUserFn(user?.access?.group);
  const isCorporateUser = user?.access?.group === "corporate";
  const isAcademicUser = user?.access?.group === "academic";
  const isAcademicDirectUser = user?.access?.group === "academic-direct";

  const redirectUrl = router.asPath;

  const handleViewContacts = () => {
    if (hasCompanyContactsPermission) {
      const fullPath = `/company/details/${window.location.pathname.split("/").slice(1)[1]}/contacts`;
      router.push(fullPath);
      return;
    }

    if (!user?.isLoggedIn) {
      router.push(`/signup?redirect=${encodeURIComponent(redirectUrl)}`);
      return;
    }

    if (isFreeUser) {
      router.push("/upgrade");
      return;
    }

    if (isSelectUser || isCorporateUser) {
      router.push("https://www.privco.com/contact");
    }
  };

  return (
    <ProfileCard>
      <ProfileCardHeader>Contacts</ProfileCardHeader>

      {isLoaded && !companyProfile?.contacts?.length ? (
        <Center flexDirection="column" py={4} minH={`${aboutHeight}px`}>
          <Tooltip
            isDisabled={!isInternational}
            label="Requesting updates for international companies is currently not available"
            shouldWrapChildren
          >
            <Button
              colorScheme="blue"
              mt={4}
              onClick={() => {
                openRequestUpdateModal({
                  options: ["overview"],
                  message: "Contacts",
                });
              }}
              isDisabled={isInternational}
              data-cy="click-contacts-update-button"
            >
              Request Update
            </Button>
          </Tooltip>
        </Center>
      ) : (
        <CardTable
          containerProps={{
            overflowY: "auto",
            maxH: `${maxContactsHeight}px`,
          }}
        >
          <CardThead>
            <CardTr>
              <CardTh>Name</CardTh>
              <CardTh>Title</CardTh>
              {!hasCompanyContactViewPermission &&
              (isAcademicUser || isAcademicDirectUser) ? (
                <CardTh> </CardTh>
              ) : (
                <CardTh>Email / Phone</CardTh>
              )}
            </CardTr>
          </CardThead>

          <CardTbody>
            {isLoaded ? (
              companyProfile.contacts.map((contact) => (
                <CardTr key={contact.hash}>
                  <CardTd>
                    <PermissionField permission="companyOverviewContacts">
                      {contact.name}
                    </PermissionField>
                  </CardTd>

                  <CardTd>
                    <PermissionField permission="companyOverviewContacts">
                      {contact.jobTitles.length !== 0 &&
                      contact.jobTitles[0] === "placeholder" ? (
                        contact.jobTitles
                      ) : (
                        <VirtualizedTableTagCollection
                          tags={contact.jobTitles.map((jobTitle) => ({
                            id: jobTitle,
                            name: jobTitle,
                          }))}
                        />
                      )}
                    </PermissionField>
                  </CardTd>

                  {!hasCompanyContactViewPermission &&
                  (isAcademicUser || isAcademicDirectUser) ? (
                    <CardTd> </CardTd>
                  ) : (
                    <CardTd>
                      {contact.hasBusinessEmail ||
                      contact.hasDirectOrPersonalNumber ||
                      contact.hasLinkedin ? (
                        <HStack spacing={3}>
                          <ContactInfoBtn contact={contact.hash} />
                          {contact.hasBusinessEmail &&
                            hasCompanyOverviewContactsPermission &&
                            hasCompanyContactViewPermission && <MailIcon />}
                          {contact.hasDirectOrPersonalNumber &&
                            hasCompanyOverviewContactsPermission &&
                            hasCompanyContactViewPermission && <PhoneIconContact />}
                          {contact.hasLinkedin &&
                            hasCompanyOverviewContactsPermission &&
                            hasCompanyContactViewPermission && <LinkedinSquareLogo />}
                        </HStack>
                      ) : (
                        "No Contact Info"
                      )}
                    </CardTd>
                  )}
                </CardTr>
              ))
            ) : (
              <SkeletonRows />
            )}
          </CardTbody>
        </CardTable>
      )}

      {!hasCompanyContactsPermission && (isFreeUser || isPublicUser) && (
        <center>
          <Button
            onClick={handleViewContacts}
            colorScheme="blue"
            size="sm"
            borderRadius="20"
            fontWeight="bolder"
            mt="6"
            px={[4, 5]}
          >
            {isPublicUser
              ? "Sign up to view Company Contact List"
              : "Upgrade now to view Company Contact List"}
          </Button>
        </center>
      )}

      {!hasCompanyContactsPermission && (isSelectUser || isCorporateUser) && (
        <center>
          <Button
            onClick={handleViewContacts}
            colorScheme="blue"
            size="sm"
            borderRadius="20"
            fontWeight="bolder"
            mt="6"
            px={[4, 5]}
          >
            Contact Sales to unlock and view Company Contact List
          </Button>
        </center>
      )}

      {isLoaded &&
        companyProfile?.contacts?.length !== 0 &&
        hasCompanyContactsPermission && (
          <center>
            <Button
              onClick={handleViewContacts}
              colorScheme="blue"
              size="sm"
              borderRadius="20"
              fontWeight="bolder"
              mt="6"
              px={[4, 5]}
            >
              View More
            </Button>
          </center>
        )}
    </ProfileCard>
  );
};

export default ContactsCard;
